import { useState } from 'react';
import { api, ApiError, type TitularExportResponse } from '../lib/api';

/**
 * Pedidos do titular (LGPD).
 *
 * A rota de exportação e o comando de apagamento existiam sem nada na tela que
 * os chamasse, e rota sem caminho na UI não está pronta: a pessoa que atende o
 * pedido não tem terminal.
 *
 * Duas coisas moldam esta tela:
 *
 * 1. **não é autoatendimento.** O titular não tem conta aqui: quem opera é quem
 *    recebeu o pedido por fora. Então a tela é de ATENDIMENTO, e diz isso;
 * 2. **o apagamento não tem desfazer.** O handle aparece confirmado antes, o
 *    motivo é obrigatório, e a confirmação exige digitar o handle de novo. Não é
 *    burocracia: apagar o handle errado por um caractere trocado não volta.
 */

type Etapa = 'inicio' | 'exportado' | 'apagado';

/**
 * Mensagem para a tela, com reserva.
 *
 * A primeira versao era `e instanceof ApiError ? e.message : reserva`, e ela
 * tinha um buraco: erro com mensagem VAZIA guardava string vazia, e o guard
 * `{erro ? ... : null}` nao renderizava nada. A falha ficava silenciosa, que e
 * o oposto do que a tela existe para fazer.
 */
function mensagemDeErro(e: unknown, reserva: string): string {
  if (e instanceof ApiError && e.message.trim().length > 0) return e.message;
  return reserva;
}

export function Titular() {
  const [handle, setHandle] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [motivo, setMotivo] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [etapa, setEtapa] = useState<Etapa>('inicio');
  const [exportacao, setExportacao] = useState<TitularExportResponse | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const handleLimpo = handle.trim();

  async function exportar() {
    setErro(null);
    setOcupado(true);
    try {
      const r = await api.exportarTitular({ handle: handleLimpo, justification: justificativa });
      setExportacao(r);
      setEtapa('exportado');
    } catch (e) {
      setErro(mensagemDeErro(e, 'Falha ao exportar.'));
    } finally {
      setOcupado(false);
    }
  }

  async function apagar() {
    setErro(null);
    setOcupado(true);
    try {
      const r = await api.sendCommand({
        command_type: 'EraseDataSubject',
        // O alvo NAO leva o handle. `ADMIN_COMMAND_REQUESTED` grava o
        // `target_ref` no payload de um evento append-only, e o handle ali
        // sobreviveria ao apagamento: o proprio recibo o ressuscitaria. Ele vai
        // so no payload do comando, que e tabela mutavel e o handler limpa
        // depois de apagar.
        target_ref: { object_type: 'DataSubject', object_id: 'informado-no-payload' },
        reason: motivo,
        payload: { handle: handleLimpo },
      });
      // O estado do comando é o que manda, e não o status HTTP: `applied` e
      // `partially_applied` são coisas diferentes, e a segunda significa que o
      // pedido continua aberto.
      setResultado(`${r.execution?.state ?? r.status}: ${r.execution?.summary ?? ''}`);
      setEtapa('apagado');
    } catch (e) {
      setErro(mensagemDeErro(e, 'Falha ao apagar.'));
    } finally {
      setOcupado(false);
    }
  }

  /** O JSON que a pessoa envia ao titular. */
  function baixar() {
    if (!exportacao) return;
    const blob = new Blob([JSON.stringify(exportacao, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Nome do arquivo SEM o handle: ele viraria PII no nome de um arquivo que
    // circula por e-mail e por pasta compartilhada.
    a.download = `exportacao-titular-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totalExportado = exportacao?.rows_returned ?? 0;

  return (
    <section aria-labelledby="titulo-titular">
      <h1 id="titulo-titular" className="text-lg font-semibold">
        Pedidos do titular
      </h1>
      <p className="mt-1 max-w-3xl text-sm text-ink-muted">
        Quem tem conta aqui é a equipe. As pessoas cujos dados o sistema guarda não têm login: o
        pedido chega por fora, e esta tela é o caminho para atendê-lo. O identificador é o handle
        público, que é o que liga os registros entre si.
      </p>

      <div className="mt-4 max-w-3xl space-y-3 rounded-card border border-line bg-card p-4 shadow-card">
        <label className="block text-sm">
          <span className="text-xs text-ink-muted">Handle do titular</span>
          <input
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@pessoa"
            className="mt-1 w-full rounded border border-line-strong px-2 py-1"
          />
        </label>

        <label className="block text-sm">
          <span className="text-xs text-ink-muted">
            Justificativa do acesso (obrigatória, fica no registro)
          </span>
          <textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            rows={2}
            placeholder="pedido de acesso recebido por e-mail em ..."
            className="mt-1 w-full rounded border border-line-strong px-2 py-1"
          />
        </label>

        <button
          type="button"
          disabled={ocupado || handleLimpo.length === 0 || justificativa.trim().length < 10}
          onClick={() => void exportar()}
          className="rounded border border-line-strong px-3 py-1 text-sm disabled:opacity-50"
        >
          Exportar os dados deste titular
        </button>
        <p className="text-xs text-ink-faint">
          A exportação devolve tudo que o sistema guarda sobre a pessoa, num documento só. Ela
          registra quem acessou, quando e por quê.
        </p>
      </div>

      {erro ? (
        <p
          role="alert"
          className="mt-3 max-w-3xl rounded-[11px] border border-line bg-surface-sunken p-3 text-sm text-warn-ink"
        >
          {erro}
        </p>
      ) : null}

      {etapa !== 'inicio' && exportacao ? (
        <div className="mt-4 max-w-3xl rounded-card border border-line bg-card p-4 shadow-card">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Resultado da exportação
          </h2>
          <p className="mt-1 text-sm">
            {totalExportado === 0
              ? 'Nenhum registro. "Não temos nada sobre você" é uma resposta legítima ao titular, e ela também fica registrada.'
              : `${totalExportado} registro(s): ${exportacao.interactions.length} interação(ões), ${exportacao.responses.length} resposta(s), ${exportacao.audience_signals.length} sinal(is) de audiência, ${exportacao.commercial_leads.length} lead(s).`}
          </p>

          <button
            type="button"
            onClick={baixar}
            className="mt-2 rounded border border-line-strong px-3 py-1 text-sm"
          >
            Baixar o JSON
          </button>

          <h3 className="mt-4 text-xs font-semibold text-ink-muted">
            O que este documento NÃO contém
          </h3>
          {/*
            Os limites viajam com o documento, e aparecem na tela também: export
            que omite em silencio e pior que export incompleto, porque quem
            recebe assume que aquilo e tudo.
          */}
          <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-ink-muted">
            {exportacao.limits.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {etapa !== 'inicio' ? (
        <div className="mt-4 max-w-3xl space-y-3 rounded border border-danger-border bg-danger-surface p-4">
          <h2 className="text-sm font-semibold text-danger-ink">
            Apagamento, e ele não tem desfazer
          </h2>
          <p className="text-xs text-danger-ink">
            O handle sai das três colunas onde ele vive e é trocado por um pseudônimo aleatório. As
            linhas e o histórico ficam: apagar a interação levaria com ela a prova de que a intenção
            comercial partiu da pessoa. O pseudônimo é aleatório de propósito, então não existe
            caminho de volta.
          </p>

          <label className="block text-sm">
            <span className="text-xs text-danger-ink">Motivo (obrigatório, fica na auditoria)</span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              placeholder="pedido de apagamento recebido por ... em ..."
              className="mt-1 w-full rounded border border-danger-border px-2 py-1"
            />
          </label>

          <label className="block text-sm">
            <span className="text-xs text-danger-ink">
              Digite o handle de novo para confirmar: <strong>{handleLimpo}</strong>
            </span>
            <input
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              className="mt-1 w-full rounded border border-danger-border px-2 py-1"
            />
          </label>

          <button
            type="button"
            disabled={
              ocupado ||
              motivo.trim().length < 10 ||
              // A confirmação compara com o handle EXATO. Não é burocracia:
              // apagar o handle errado por um caractere trocado não volta.
              confirmacao.trim() !== handleLimpo
            }
            onClick={() => void apagar()}
            className="rounded border border-danger-border bg-danger-surface px-3 py-1 text-sm text-danger-ink disabled:opacity-50"
          >
            Apagar os dados deste titular
          </button>
        </div>
      ) : null}

      {resultado ? (
        <p className="mt-3 max-w-3xl rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          {resultado}
        </p>
      ) : null}
    </section>
  );
}
